import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom'; 
import './Sidebar.css';
import {
  PlayIcon,
  ClockIcon,
  Cog6ToothIcon,
  DocumentCheckIcon,
  ClipboardDocumentListIcon,
  WrenchScrewdriverIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FolderIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { SidebarProps, Project } from '../../types';

const Sidebar: React.FC<SidebarProps> = ({
  activeSection,
  onSectionChange,
  selectedProject,
  projectList = [],
  onProjectSelect
}) => {
  const navigate = useNavigate();
  const [expandedProjects, setExpandedProjects] = useState<Record<number, boolean>>({});

  // State for sidebar collapse and width
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [sidebarWidth, setSidebarWidth] = useState<number>(256); // Default width 256px (64 rem)
  const collapsedWidth = 64; // Width when collapsed

  // State for resizing
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const minWidth = 200;
  const maxWidth = 400;

  // Load sidebar state from localStorage on component mount
  useEffect(() => {
    const savedIsCollapsed = localStorage.getItem('sidebarCollapsed');
    const savedWidth = localStorage.getItem('sidebarWidth');

    if (savedIsCollapsed) {
      setIsCollapsed(savedIsCollapsed === 'true');
    }

    if (savedWidth && !isCollapsed) {
      setSidebarWidth(parseInt(savedWidth, 10));
    }
  }, []);

  // Save sidebar state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isCollapsed.toString());
    if (!isCollapsed) {
      localStorage.setItem('sidebarWidth', sidebarWidth.toString());
    }
  }, [isCollapsed, sidebarWidth]);

  // Keep CSS variable --sidebar-width in sync so layout can offset dynamically
  useEffect(() => {
    const currentWidth = isCollapsed ? collapsedWidth : sidebarWidth;
    document.documentElement.style.setProperty('--sidebar-width', `${currentWidth}px`);
  }, [isCollapsed, sidebarWidth]);

  // Toggle sidebar collapse state
  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Mouse event handlers for resizing
  const startResizing = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResizing);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing) return;

    // Calculate new width based on mouse position
    const newWidth = e.clientX;

    // Apply min/max constraints
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setSidebarWidth(newWidth);
    }
  };

  const stopResizing = () => {
    setIsResizing(false);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', stopResizing);
  };

  // Clean up event listeners
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing]);

  // Base menu items (without project-specific items)
  const baseMenuItems = [
    {
      id: 'projects',
      name: 'Projects',
      icon: PlusIcon,
      description: 'Create and manage projects',
      path: '/projects'
    }
  ];

  // Project-specific menu items (shown for each project)
  const projectMenuItems = [
    {
      id: 'metadata-management',
      name: 'Metadata Management',
      icon: PlayIcon,
      description: 'Manage and validate metadata files',
      path: '/metadata-management'
    },
    {
      id: 'push-job',
      name: 'Workflows',
      icon: DocumentCheckIcon,
      description: 'Create and Manage data processing workflows',
      path: '/push-job'
    },
    {
      id: 'job-monitoring',
      name: 'Workflows Monitoring',
      icon: ClockIcon,
      description: 'Monitor running and completed workflows',
      path: '/job-monitoring'
    },
    {
      id: 'settings',
      name: 'Settings',
      icon: Cog6ToothIcon,
      description: 'Configure quality attribute thresholds',
      path: '/settings'
    }
  ];

  const handleNavigation = (item: typeof baseMenuItems[0]) => {
    navigate(item.path);
    onSectionChange(item.id); // Keep for compatibility
  };

  const navigateToDashboard = () => {
    navigate('/');
    onSectionChange('dashboard'); // Keep for compatibility
  };

  const handleProjectClick = (project: Project) => {
    // Select the project (Open Project behavior)
    if (onProjectSelect) {
      console.log('Selecting project from sidebar:', project.name);
      onProjectSelect(project);
    }
    
    // Always expand the project when selected
    setExpandedProjects(prev => ({
      ...prev,
      [project.id]: true
    }));
    
    // Navigate to the metadata management page (first page in project workflow)
    navigate(`/metadata-management?projectId=${project.id}`);
    onSectionChange('metadata-management');
  };

  const handleProjectMenuClick = (project: Project, menuPath: string, menuId: string) => {
    // Select the project (Open Project behavior)
    if (onProjectSelect) {
      console.log('Selecting project from menu item:', project.name);
      onProjectSelect(project);
    }
    // Include projectId in URL to ensure refetching and deep-linking per project
    navigate(`${menuPath}?projectId=${project.id}`);
    onSectionChange(menuId);
  };

  const toggleProjectExpanded = (projectId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  // Initialize expanded state for the selected project if not already set
  useEffect(() => {
    if (selectedProject && expandedProjects[selectedProject.id] === undefined) {
      setExpandedProjects(prev => ({
        ...prev,
        [selectedProject.id]: true
      }));
    }
  }, [selectedProject, expandedProjects]);

  // Check if a menu item belongs to the selected project
  const isMenuItemActive = (projectId: number, menuId: string) => {
    return selectedProject?.id === projectId && activeSection === menuId;
  };

  // Debug logging for selected project
  useEffect(() => {
    if (selectedProject) {
      console.log('Selected project in sidebar:', selectedProject.name);
    }
  }, [selectedProject]);

  return (
    <>
      <div 
        ref={sidebarRef}
        className="h-screen flex flex-col overflow-hidden relative"
        style={{ 
          width: isCollapsed ? `${collapsedWidth}px` : `${sidebarWidth}px`,
          backgroundColor: '#0f172a', 
          borderRight: '1px solid #334155',
          transition: isResizing ? 'none' : 'width 0.3s ease',
          cursor: isResizing ? 'col-resize' : 'auto'
        }}
      >
        {/* Sidebar content */}
        <div className="h-screen flex flex-col overflow-auto scroll-container">
          {/* Header - Now clickable */}
          <div 
            className="p-6 cursor-pointer hover:bg-slate-800 transition-colors duration-200 flex items-center justify-between" 
            style={{ borderBottom: '1px solid #334155' }}
          >
            <div className="flex items-center space-x-3" onClick={navigateToDashboard}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)' }}>
                <span className="text-white font-bold text-lg">D</span>
              </div>
              {!isCollapsed && (
                <div>
                  <h1 className="text-xl font-bold" style={{ color: '#f1f5f9' }}>DAP Console</h1>
                  <p className="text-sm" style={{ color: '#94a3b8' }}>Data Analytics Platform</p>
                </div>
              )}
            </div>
            
            {/* Toggle button - Only shown when sidebar is expanded */}
            {!isCollapsed && (
              <button 
                onClick={toggleSidebar}
                className="p-2 rounded-lg hover:bg-slate-700 transition-colors"
                title="Collapse sidebar"
              >
                <ChevronDoubleLeftIcon className="w-5 h-5 text-gray-400" />
              </button>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {/* Base menu items */}
            {baseMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item)}
                  className={`w-full text-left p-4 rounded-xl transition-all duration-200 group border-2 ${isCollapsed ? 'flex justify-center' : ''}`}
                  style={{
                    backgroundColor: isActive ? '#1e293b' : 'transparent',
                    borderColor: isActive ? '#0ea5e9' : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = '#1e293b';
                      e.currentTarget.style.borderColor = '#475569';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = 'transparent';
                    }
                  }}
                  title={isCollapsed ? item.name : undefined}
                >
                  {isCollapsed ? (
                    <Icon 
                      className="w-6 h-6 transition-colors duration-200"
                      style={{
                        color: isActive ? '#0ea5e9' : '#64748b'
                      }}
                    />
                  ) : (
                    <div className="flex items-start space-x-3">
                      <Icon 
                        className="w-6 h-6 mt-0.5 transition-colors duration-200"
                        style={{
                          color: isActive ? '#0ea5e9' : '#64748b'
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <h3 
                          className="font-medium text-sm transition-colors duration-200"
                          style={{
                            color: isActive ? '#f1f5f9' : '#cbd5e1'
                          }}
                        >
                          {item.name}
                        </h3>
                        <p 
                          className="text-xs mt-1 transition-colors duration-200"
                          style={{
                            color: isActive ? '#94a3b8' : '#64748b'
                          }}
                        >
                          {item.description}
                        </p>
                      </div>
                    </div>
                  )}
                </button>
              );
            })}

            {/* Projects Section with Sub-menus */}
            {projectList && projectList.length > 0 && (
              <div className="mt-4 space-y-2">
                {projectList.map((project) => {
                  const isSelected = selectedProject?.id === project.id;
                  const isExpanded = expandedProjects[project.id] || false;
                  
                  // Check if any menu item of this project is active
                  const hasActiveMenuItem = projectMenuItems.some(menuItem => 
                    isMenuItemActive(project.id, menuItem.id)
                  );
                  
                  // Project should be highlighted if it's selected or has an active menu item
                  const shouldHighlight = isSelected || hasActiveMenuItem;
                  
                  return (
                    <div key={project.id} className="rounded-lg overflow-hidden">
                      <button
                        onClick={() => handleProjectClick(project)}
                        className={`w-full text-left p-3 rounded-lg flex items-center justify-between transition-colors duration-200 ${
                          shouldHighlight ? 'bg-blue-900 bg-opacity-20 border-2 border-blue-500' : 'hover:bg-slate-800'
                        }`}
                        title={isCollapsed ? project.name : undefined}
                      >
                        <div className={`flex items-center ${isCollapsed ? 'justify-center w-full' : 'space-x-2'}`}>
                          <FolderIcon 
                            className="w-5 h-5 flex-shrink-0" 
                            style={{ color: shouldHighlight ? '#38bdf8' : '#64748b' }} 
                          />
                          {!isCollapsed && (
                            <span 
                              className="text-sm font-medium truncate"
                              style={{ color: shouldHighlight ? '#f1f5f9' : '#94a3b8' }}
                            >
                              {project.name}
                            </span>
                          )}
                        </div>
                        {!isCollapsed && (
                          <div 
                            onClick={(e) => toggleProjectExpanded(project.id, e)}
                            className="p-1 hover:bg-blue-800 hover:bg-opacity-30 rounded"
                          >
                            {isExpanded ? (
                              <ChevronDownIcon className="w-4 h-4" style={{ color: shouldHighlight ? '#38bdf8' : '#64748b' }} />
                            ) : (
                              <ChevronRightIcon className="w-4 h-4" style={{ color: shouldHighlight ? '#38bdf8' : '#64748b' }} />
                            )}
                          </div>
                        )}
                      </button>
                      
                      {/* Project Sub-menu Items - Only show when not collapsed or when expanded in collapsed mode */}
                      {!isCollapsed && isExpanded && (
                        <div className="ml-4 mt-1 space-y-1">
                          {projectMenuItems.map((subItem) => {
                            const SubIcon = subItem.icon;
                            const isSubActive = isMenuItemActive(project.id, subItem.id);
                            
                            return (
                              <button
                                key={subItem.id}
                                onClick={() => handleProjectMenuClick(project, subItem.path, subItem.id)}
                                className={`w-full text-left p-2 rounded-lg flex items-center space-x-2 transition-colors duration-200 ${
                                  isSubActive ? 'bg-blue-900 bg-opacity-30' : 'hover:bg-slate-800'
                                }`}
                              >
                                <SubIcon 
                                  className="w-4 h-4 flex-shrink-0" 
                                  style={{ color: isSubActive ? '#38bdf8' : '#64748b' }} 
                                />
                                <span 
                                  className="text-sm truncate"
                                  style={{ color: isSubActive ? '#f1f5f9' : '#94a3b8' }}
                                >
                                  {subItem.name}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </nav>

          {/* Footer */}
          {!isCollapsed && (
            <div className="p-4" style={{ borderTop: '1px solid #334155' }}>
              <div className="rounded-lg p-3" style={{ backgroundColor: '#1e293b' }}>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                  <span className="text-xs font-medium" style={{ color: '#cbd5e1' }}>System Online</span>
                </div>
                <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>
                  All services are operational
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Custom resize handle */}
        {!isCollapsed && (
          <div 
            className="resize-handle"
            onMouseDown={startResizing}
            onClick={e => e.stopPropagation()}
          />
        )}
      </div>
      
      {/* Floating expand button - Only shown when sidebar is collapsed */}
      {isCollapsed && (
        <button
          onClick={toggleSidebar}
          className="floating-expand-button"
          title="Expand sidebar"
        >
          <ChevronDoubleRightIcon className="w-5 h-5" />
        </button>
      )}
    </>
  );
};

export default Sidebar;
